import { useRouter } from 'expo-router';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchAcceptChallenge, {
  AcceptChallengeData,
} from 'features/auth/resetPassword/utils/fetchAcceptChallenge/fetchAcceptChallenge';
import {
  InformationScreenTypeEnum,
  screenLevelErrorMessageCodes,
} from 'features/otp/information/InformationScreenTypeEnum';
import { useContext, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { getClientErrorResponse } from 'shared/utils/errors/getMaxLimitErrorResponse';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export interface AnalyticsProps {
  isPhoneChallenge?: boolean | undefined;
}

export function useAcceptChallenge() {
  const router = useRouter();
  const [isLoading, setLoading] = useState(false);
  const { getVaroSessionId, idToken } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.ACCEPT_CHALLENGE_USE_K8S_AUTH_SERVICE,
  );

  const acceptChallenge = async (
    data: AcceptChallengeData,
    analyticsProps?: AnalyticsProps,
  ) => {
    setLoading(true);

    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    try {
      const {
        challengeVerified = null,
        errorCode,
        message,
        title,
      } = await fetchAcceptChallenge(
        authUrl,
        {
          ...data,
        },
        headers,
      );

      setLoading(false);
      if (challengeVerified === true) {
        router.navigate({
          pathname: '/reset-password/set-password',
          params: {
            challengeId: data.challengeId,
            identityId: data.identityId,
            email: data.email,
          },
        });
      } else {
        if (errorCode) {
          if (screenLevelErrorMessageCodes.includes(errorCode.toString())) {
            router.navigate({
              pathname: '/otp/error',
              params: {
                errorCode: errorCode,
                message: message,
                title: title,
                isPhoneChallenge:
                  analyticsProps?.isPhoneChallenge?.toString() ?? '',
              },
            });
          } else {
            return {
              errorCode: errorCode,
              message: message,
              title: title,
            };
          }
        }
      }
    } catch (error) {
      const incorrectCodeResponse = getClientErrorResponse(error, 5);
      if (incorrectCodeResponse) {
        setLoading(false);
        return {
          errorCode: 5,
          message: incorrectCodeResponse.message,
          title: incorrectCodeResponse.title,
        };
      }

      const expiredCodeResponse = getClientErrorResponse(error, 6);
      if (expiredCodeResponse && expiredCodeResponse.title) {
        setLoading(false);
        router.navigate({
          pathname: '/otp/error',
          params: {
            errorCode: expiredCodeResponse.errorCode,
            message: expiredCodeResponse.message,
            title: expiredCodeResponse.title,
            isPhoneChallenge:
              analyticsProps?.isPhoneChallenge?.toString() ?? '',
          },
        });
        return;
      }

      logErrorWithContext('AcceptChallenge error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.navigate({
        pathname: '/otp/error',
        params: {
          screenType: InformationScreenTypeEnum.Error,
          isPhoneChallenge: analyticsProps?.isPhoneChallenge?.toString() ?? '',
        },
      });
    }
    setLoading(false);
  };

  return {
    acceptChallenge,
    isLoading,
  };
}

export const getErrorScreen = (errorCode: string | undefined) => {
  if (!errorCode) {
    return InformationScreenTypeEnum.Error;
  }
  switch (errorCode) {
    case '6':
      return InformationScreenTypeEnum.LinkExpired;
    case '4':
      return InformationScreenTypeEnum.MaxLimitReached;
    default:
      return InformationScreenTypeEnum.Error;
  }
};
